import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, FileText, Pencil, Key, UserCheck, UserX, Trash2, Printer, Download } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { COMPANY } from "@/lib/brand";

const peso = (n) => `₱${(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2 })}`;

const CustomerDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [credit, setCredit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statement, setStatement] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [c, t, cr] = await Promise.all([
        api.get(`/admin/customers/${id}`),
        api.get(`/admin/customers/${id}/transactions`),
        api.get(`/admin/customers/${id}/credit-transactions`),
      ]);
      setCustomer(c.data); setTransactions(t.data); setCredit(cr.data);
    } catch (e) {
      toast.error("Failed to load customer");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, t, cr] = await Promise.all([
          api.get(`/admin/customers/${id}`),
          api.get(`/admin/customers/${id}/transactions`),
          api.get(`/admin/customers/${id}/credit-transactions`),
        ]);
        if (!cancelled) {
          setCustomer(c.data); setTransactions(t.data); setCredit(cr.data);
        }
      } catch {
        if (!cancelled) toast.error("Failed to load customer");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const openEdit = () => {
    setForm({ ...customer, password: "" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { password, email, user_id, role, ...rest } = form;
      const body = { ...rest, credit_limit: parseFloat(form.credit_limit) || 0 };
      if (password) body.password = password;
      await api.patch(`/admin/customers/${id}`, body);
      toast.success("Customer updated");
      setEditOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const resetPassword = async () => {
    if (!window.confirm(`Reset password for ${customer.name}?`)) return;
    try {
      const { data } = await api.post(`/admin/customers/${id}/reset-password`);
      window.prompt("Temporary password (copy and share securely):", data.temp_password);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const toggleActive = async () => {
    try {
      await api.patch(`/admin/customers/${id}`, { active: customer.active === false });
      toast.success(customer.active === false ? "Enabled" : "Disabled");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete customer ${customer.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/customers/${id}`);
      toast.success("Deleted");
      navigate("/admin/customers");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const toggleCredit = async (enabled) => {
    try {
      await api.patch(`/admin/customers/${id}`, { credit_line_enabled: enabled });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const generateSOA = async () => {
    try {
      const { data } = await api.get(`/admin/customers/${id}/statement`);
      setStatement(data);
    } catch { toast.error("Failed to generate SOA"); }
  };

  const downloadSOAPDF = async () => {
    try {
      const res = await api.get(`/admin/customers/${id}/statement-pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = `SOA-${customer.name.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
      toast.success("SOA PDF downloaded");
    } catch { toast.error("Failed to download PDF"); }
  };

  if (loading) return <div className="p-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;
  if (!customer) return <div className="p-12 text-center text-neutral-500">Customer not found.</div>;

  const outstanding = credit.filter((p) => p.payment_status === "unpaid").reduce((a, b) => a + (b.amount || 0), 0);

  return (
    <div className="p-6 lg:p-10 pav-fade-in max-w-[1400px]" data-testid="customer-detail-page">
      <div className="mb-6">
        <Link to="/admin/customers" className="text-sm text-neutral-500 hover:text-[#0B1E3F] inline-flex items-center gap-1.5">
          <ArrowLeft className="h-4 w-4" /> Back to customers
        </Link>
        <div className="flex items-start justify-between gap-3 mt-2 flex-wrap">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-[#0B1E3F]">{customer.name}</h1>
            {customer.company && <p className="text-neutral-500 mt-0.5">{customer.company}</p>}
            <div className="flex items-center gap-2 mt-2">
              <span className="pav-chip capitalize">{(customer.customer_type || "walk_in").replace("_", " ")}</span>
              <span className={`pav-chip ${customer.active === false ? "status-cancelled" : "status-completed"}`}>
                {customer.active === false ? "disabled" : "active"}
              </span>
              {customer.credit_line_enabled && <span className="pav-chip status-paid">credit line</span>}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={openEdit} className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="customer-edit-button">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
            <button onClick={resetPassword} className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="customer-reset-password-button">
              <Key className="h-3.5 w-3.5" /> Reset password
            </button>
            <button onClick={toggleActive} className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="customer-toggle-active-button">
              {customer.active === false ? <UserCheck className="h-3.5 w-3.5" /> : <UserX className="h-3.5 w-3.5" />}
              {customer.active === false ? "Enable" : "Disable"}
            </button>
            <button onClick={remove} className="btn-outline text-sm text-red-600 hover:bg-red-50 inline-flex items-center gap-1.5" data-testid="customer-delete-button">
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="pav-card" data-testid="info-email">
          <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Email</p>
          <p className="font-semibold mt-1 truncate">{customer.email}</p>
        </div>
        <div className="pav-card" data-testid="info-phone">
          <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Phone</p>
          <p className="font-semibold mt-1">{customer.phone || "—"}</p>
        </div>
        <div className="pav-card" data-testid="info-tin">
          <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">TIN</p>
          <p className="font-semibold mt-1">{customer.tin || "—"}</p>
        </div>
        <div className="pav-card" data-testid="info-outstanding">
          <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Outstanding (Credit)</p>
          <p className={`font-display font-extrabold text-xl mt-1 ${outstanding > 0 ? "text-red-600" : "text-[#0B1E3F]"}`}>{peso(outstanding)}</p>
        </div>
        <div className="pav-card md:col-span-2 lg:col-span-4">
          <p className="text-xs uppercase tracking-wider text-neutral-500 font-semibold">Address</p>
          <p className="font-semibold mt-1">{customer.address || "—"}</p>
        </div>
      </div>

      <Tabs defaultValue="transactions">
        <TabsList className="bg-[#F4F6FB] mb-4">
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions ({transactions.length})</TabsTrigger>
          <TabsTrigger value="credit" data-testid="tab-credit">Credit Line ({credit.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions">
          <div className="pav-card p-0 overflow-hidden">
            {transactions.length === 0 ? (
              <p className="p-10 text-center text-neutral-500">No transactions yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F6FB] text-xs uppercase tracking-wider text-neutral-600">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Order</th>
                      <th className="text-left px-3 py-2">Services</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Payment</th>
                      <th className="text-right px-3 py-2">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {transactions.map((o) => (
                      <tr key={o.order_id} className="hover:bg-[#F8FAFC]" data-testid={`tx-row-${o.order_id}`}>
                        <td className="px-3 py-2 text-neutral-600">{new Date(o.created_at).toLocaleString()}</td>
                        <td className="px-3 py-2 font-semibold">{o.order_id}</td>
                        <td className="px-3 py-2 text-xs">{(o.items || []).map((it, i) => <p key={i}>{it.service_name || it.category} ×{it.quantity}</p>)}</td>
                        <td className="px-3 py-2"><span className={`pav-chip status-${o.status}`}>{o.status?.replace(/_/g, " ")}</span></td>
                        <td className="px-3 py-2 capitalize">
                          {(o.payment_mode || "—").replace("_", " ")}
                          {o.payment_status === "unpaid" && <span className="ml-1 text-red-600 text-[10px] uppercase">unpaid</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-bold">{peso(o.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="credit">
          <div className="pav-card mb-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-sm font-semibold">Credit Line</p>
                <p className="text-xs text-neutral-500 mt-1">Enable to allow On Account payments. Credit limit applies if &gt; 0.</p>
              </div>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!customer.credit_line_enabled}
                    onChange={(e) => toggleCredit(e.target.checked)}
                    data-testid="credit-line-toggle" />
                  Credit-line eligible
                </label>
                <button onClick={generateSOA} className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="generate-soa-button">
                  <FileText className="h-3.5 w-3.5" /> Generate SOA
                </button>
                <button onClick={downloadSOAPDF} className="btn-accent text-sm inline-flex items-center gap-1.5" data-testid="download-soa-pdf-button">
                  <Download className="h-3.5 w-3.5" /> Download PDF
                </button>
              </div>
            </div>
          </div>

          <div className="pav-card p-0 overflow-hidden mb-4">
            {credit.length === 0 ? (
              <p className="p-10 text-center text-neutral-500">No credit transactions.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F6FB] text-xs uppercase tracking-wider text-neutral-600">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Order</th>
                      <th className="text-right px-3 py-2">Amount</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {credit.map((p) => (
                      <tr key={p.transaction_id} data-testid={`credit-row-${p.transaction_id}`}>
                        <td className="px-3 py-2 text-neutral-600">{new Date(p.created_at).toLocaleString()}</td>
                        <td className="px-3 py-2 font-semibold">{p.order_id}</td>
                        <td className="px-3 py-2 text-right font-bold">{peso(p.amount)}</td>
                        <td className="px-3 py-2">
                          <span className={`pav-chip ${p.payment_status === "paid" ? "status-completed" : "status-cancelled"}`}>
                            {p.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {statement && (
            <div className="border-2 border-[#0B1E3F] rounded-xl p-6 bg-white print:border-0" data-testid="soa-document">
              <div className="flex items-start justify-between mb-6 print:mb-4">
                <div>
                  <h2 className="font-display text-2xl font-extrabold text-[#0B1E3F]">STATEMENT OF ACCOUNT</h2>
                  <p className="text-xs text-neutral-500 mt-1">{statement.soa_number}</p>
                </div>
                <div className="text-right text-xs text-neutral-700">
                  <p className="font-bold">{COMPANY.legal}</p>
                  <p>{COMPANY.address}</p>
                  <p>{COMPANY.email} · {COMPANY.phone}</p>
                </div>
              </div>
              <div className="mb-4 text-sm">
                <p><span className="text-neutral-500">Customer:</span> <span className="font-semibold">{statement.customer.name}</span></p>
                <p><span className="text-neutral-500">Email:</span> {statement.customer.email}</p>
                <p><span className="text-neutral-500">TIN:</span> {statement.customer.tin || "—"}</p>
                <p><span className="text-neutral-500">As of:</span> {new Date(statement.generated_at).toLocaleString()}</p>
              </div>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-y-2 border-[#0B1E3F]">
                    <th className="text-left py-2">Date</th>
                    <th className="text-left py-2">Order</th>
                    <th className="text-left py-2">Description</th>
                    <th className="text-right py-2">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {statement.items.map((it, i) => (
                    <tr key={i}>
                      <td className="py-2">{new Date(it.date).toLocaleDateString()}</td>
                      <td className="py-2 font-semibold">{it.order_id}</td>
                      <td className="py-2">{it.description}</td>
                      <td className="py-2 text-right">{peso(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#0B1E3F]">
                    <td colSpan="3" className="py-3 font-display font-extrabold text-right">TOTAL DUE</td>
                    <td className="py-3 text-right font-display font-extrabold text-2xl text-[#0B1E3F]">{peso(statement.total_due)}</td>
                  </tr>
                </tfoot>
              </table>
              <div className="mt-5 flex gap-2 print:hidden">
                <button onClick={() => window.print()} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="print-soa-button">
                  <Printer className="h-4 w-4" /> Print
                </button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg" data-testid="customer-edit-dialog">
          <DialogHeader><DialogTitle>Edit customer</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Full name</label>
              <input className="pav-input mt-1" data-testid="edit-name-input"
                value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Phone</label>
              <input className="pav-input mt-1" data-testid="edit-phone-input"
                value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Company</label>
              <input className="pav-input mt-1" data-testid="edit-company-input"
                value={form.company || ""} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Address</label>
              <input className="pav-input mt-1" data-testid="edit-address-input"
                value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">TIN</label>
              <input className="pav-input mt-1" data-testid="edit-tin-input"
                value={form.tin || ""} onChange={(e) => setForm({ ...form, tin: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Customer type</label>
              <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
                <SelectTrigger className="pav-input mt-1" data-testid="edit-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="walk_in">Walk-In</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 bg-[#F4F6FB] rounded-lg p-3">
              <label className="flex items-center gap-2 text-sm font-semibold">
                <input type="checkbox" data-testid="edit-credit-checkbox"
                  checked={!!form.credit_line_enabled}
                  onChange={(e) => setForm({ ...form, credit_line_enabled: e.target.checked })} />
                Credit line eligible
              </label>
              {form.credit_line_enabled && (
                <div className="mt-2">
                  <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">Credit limit (₱, 0 = unlimited)</label>
                  <input type="number" min="0" step="100" className="pav-input mt-1"
                    data-testid="edit-credit-limit-input"
                    value={form.credit_limit ?? 0} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })} />
                </div>
              )}
            </div>
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-wider font-semibold text-neutral-600">New password (blank = unchanged)</label>
              <input type="password" className="pav-input mt-1" data-testid="edit-password-input"
                value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setEditOpen(false)} className="btn-outline text-sm">Cancel</button>
            <button onClick={saveEdit} disabled={saving} className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50" data-testid="edit-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDetailPage;
